import { useState, useEffect, useCallback, useRef } from 'react';
import styled from 'styled-components';
import { btnPrimary, btnDelete } from '../../styles/buttonStyles';
import { fetchPortfolioProfile, fetchAllLanguages, updateProfile, uploadAvatar, deleteAvatar } from './portfolioApi';
import { isAxiosError } from '../../utils/api';
import { logError } from '../../components/utils/errors';
import type { PortfolioProfile, Language } from './types';
import EditPortfolioLayout from './components/EditPortfolioLayout';
import { useGooglePlacesAutocomplete } from '../../hooks/useGooglePlacesAutocomplete';

const C = { teal: '#5bc0be', tealDark: '#4aafad', salmon: '#e8927c', red: '#c0392b', border: '#d4d8dd', borderLight: '#e6e8eb', text: '#555', textDark: '#333', muted: '#999', white: '#fff' };

const PageTitle = styled.h1`font-size: 28px; font-weight: 300; color: ${C.salmon}; margin: 0 0 24px;`;

const Divider = styled.div`border-top: 1px solid ${C.borderLight}; padding-top: 24px; margin-top: 24px;`;

const SectionTitle = styled.h3`font-size: 16px; font-weight: 400; color: #aaa; margin: 0 0 20px;`;

const Row = styled.div`margin-bottom: 16px;`;

const NameRow = styled.div`display: flex; gap: 16px; margin-bottom: 16px; > div { flex: 1; }`;

const Label = styled.label<{ $req?: boolean }>`
  display: block; font-size: 11px; font-weight: 600; text-transform: uppercase;
  letter-spacing: .5px; color: ${C.text}; margin-bottom: 6px;
  ${({ $req }) => $req && `&::after { content: ' *'; color: ${C.red}; }`}
`;

const LabelSub = styled.div`margin-bottom: 6px; ${Label} { margin-bottom: 2px; }`;

const Sub = styled.div`font-size: 12px; color: ${C.muted};`;

const Input = styled.input`
  width: 100%; padding: 8px 12px; border: 1px solid ${C.border}; border-radius: 3px;
  font-size: 14px; color: ${C.textDark}; background: ${C.white}; box-sizing: border-box;
  &:focus { outline: none; border-color: ${C.teal}; }
  &::placeholder { color: #bbb; }
`;

const Textarea = styled.textarea`
  width: 100%; padding: 8px 12px; border: 1px solid ${C.border}; border-radius: 3px;
  font-size: 14px; color: ${C.textDark}; min-height: 80px; resize: vertical; box-sizing: border-box;
  &:focus { outline: none; border-color: ${C.teal}; }
  &::placeholder { color: #bbb; }
`;

const Select = styled.select`
  width: 100%; padding: 8px 12px; border: 1px solid ${C.border}; border-radius: 3px;
  font-size: 14px; color: ${C.textDark}; background: ${C.white}; box-sizing: border-box;
  &:focus { outline: none; border-color: ${C.teal}; }
`;

const UrlRow = styled.div`display: flex; align-items: center; gap: 8px;`;

const UrlSuffix = styled.span`font-size: 14px; color: #666; white-space: nowrap;`;

const PhotoRow = styled.div`display: flex; align-items: center; gap: 16px; margin-bottom: 16px;`;

const PhotoPreview = styled.div<{ $url?: string }>`
  width: 80px; height: 80px; border-radius: 4px; flex-shrink: 0;
  background: ${({ $url }) => $url ? `url(${$url}) center/cover no-repeat` : C.borderLight};
`;

const PhotoBtns = styled.div`display: flex; flex-direction: column; gap: 6px;`;

const PhotoBtn = styled.label<{ $del?: boolean }>`
  display: inline-block; padding: 6px 14px;
  border: 1px solid ${({ $del }) => ($del ? C.red : C.teal)};
  color: ${({ $del }) => ($del ? C.red : C.teal)};
  font-size: 11px; text-transform: uppercase; letter-spacing: .5px; border-radius: 3px;
  cursor: pointer; text-align: center; font-weight: 500;
  &:hover { background: ${({ $del }) => ($del ? C.red : C.teal)}; color: ${C.white}; }
  input { display: none; }
`;

const DeleteBtn = styled.button<{ $del?: boolean }>`
  ${btnDelete}
  padding: 6px 14px;
  font-size: 11px;
`;

const SocialRow = styled.div`display: flex; align-items: center; gap: 10px; margin-bottom: 12px;`;

const SocialCircle = styled.div<{ $color: string }>`
  width: 32px; height: 32px; border-radius: 50%; background: ${({ $color }) => $color};
  display: flex; align-items: center; justify-content: center; color: ${C.white};
  flex-shrink: 0; font-size: 14px;
`;

const SocialInput = styled(Input)`flex: 1;`;

const Actions = styled.div`
  border-top: 1px solid ${C.borderLight}; padding-top: 20px; margin-top: 24px; text-align: right;
`;

const SaveBtn = styled.button`
  ${btnPrimary}
  padding: 10px 28px;
  font-size: 14px;
`;

const Toast = styled.div<{ $ok: boolean }>`
  position: fixed; bottom: 24px; right: 24px; padding: 12px 20px; border-radius: 4px;
  font-size: 14px; font-weight: 500; z-index: 1000;
  background: ${({ $ok }) => ($ok ? C.teal : C.red)}; color: ${C.white};
`;

const SOCIAL = [
  { key: 'facebookUrl' as const, placeholder: 'example: http://facebook.com/profile', color: '#3b5998', icon: 'f' },
  { key: 'twitterUrl' as const, placeholder: 'example: http://twitter.com/profile', color: '#1da1f2', icon: 't' },
  { key: 'linkedinUrl' as const, placeholder: 'example: http://linkedin.com/in/profile', color: '#0077b5', icon: 'in' },
  { key: 'tumblrUrl' as const, placeholder: 'example: http://tumblr.com/profile', color: '#35465c', icon: 't' },
  { key: 'instagramUrl' as const, placeholder: 'example: http://instagram.com/profile', color: '#e1306c', icon: 'ig' },
];

type SocialKey = typeof SOCIAL[number]['key'];

function PortfolioProfilePage() {
  const [profile, setProfile] = useState<PortfolioProfile | undefined>();
  const [languages, setLanguages] = useState<Language[]>([]);
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  // parity-audit-r2 (H2): discrete location components the BE persists (city/state/
  // country). Legacy geocoded the free-text Location field into these via Google
  // Places; the migrated form dropped them from the save payload entirely, so the
  // Location field was write-only (silent data loss). We now track them and send
  // them on save — populated either by Places autocomplete or by parsing the
  // free-text input (fallback when no geocode token is provisioned).
  const locationRef = useRef<HTMLInputElement>(null);
  const [residence, setResidence] = useState({ city: '', state: '', country: '' });

  const [form, setForm] = useState({
    firstName: '', lastName: '', email: '', company: '', freelancerType: '',
    websiteUrl: '', phoneNumber: '', location: '', timeZone: '', username: '',
    headline: '', bio: '', selectedLanguageIds: [] as number[],
    facebookUrl: '', twitterUrl: '', linkedinUrl: '', tumblrUrl: '', instagramUrl: '',
  });

  // Geocode the Location field when a Places token is provisioned (legacy parity).
  const { available: geocodeAvailable } = useGooglePlacesAutocomplete(locationRef, (place) => {
    setResidence({ city: place.city, state: place.state, country: place.country });
    setForm((f) => ({ ...f, location: place.formatted || f.location }));
  });

  const load = useCallback(async () => {
    try {
      const [p, l] = await Promise.all([fetchPortfolioProfile(), fetchAllLanguages()]);
      setProfile(p);
      setLanguages(l);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!profile) return;
    setForm({
      firstName: profile.firstName || '', lastName: profile.lastName || '',
      email: profile.email || '', company: profile.company || '',
      freelancerType: profile.freelancerType || '', websiteUrl: profile.websiteUrl || '',
      phoneNumber: profile.phoneNumber || '',
      location: [profile.city, profile.state, profile.country].filter(Boolean).join(', '),
      // (city/state/country seeded into `residence` below — see effect)
      timeZone: profile.timeZone || '', username: profile.username || '',
      headline: profile.headline || '', bio: profile.bio || '',
      selectedLanguageIds: profile.languages?.map(l => l.id) || [],
      facebookUrl: profile.socialLink?.facebookUrl || '',
      twitterUrl: profile.socialLink?.twitterUrl || '',
      linkedinUrl: profile.socialLink?.linkedinUrl || '',
      tumblrUrl: profile.socialLink?.tumblrUrl || '',
      instagramUrl: profile.socialLink?.instagramUrl || '',
    });
    // parity-audit-r2 (H2): seed discrete residence components from the profile.
    setResidence({
      city: profile.city || '', state: profile.state || '', country: profile.country || '',
    });
  }, [profile]);

  const flash = (msg: string, ok: boolean) => { setToast({ msg, ok }); setTimeout(() => setToast(null), 3000); };

  const set = (k: string, v: string | number[]) => setForm(p => ({ ...p, [k]: v }));

  // Portfolio users own a public subdomain (`<username>.contently.com`), so the
  // username field is editable + required for them, hidden for everyone else —
  // matching the legacy `if current_user.portfolio?` partial gate. `portfolio?`
  // BE-side is `username.present? && role?(PORTFOLIO)` (eng-maintenance#20940).
  const isPortfolio = !!profile?.isPortfolio;

  // Mirror the Rails User model's username validation (format /\A[\w]*\z/,
  // length 5..40). Only enforced for portfolio users, matching legacy's
  // `required: true, minlength: 5, maxlength: 40` on the field.
  const usernameError = (): string | null => {
    const u = form.username.trim();
    if (u.length < 5 || u.length > 40) return 'Your portfolio URL must be 5–40 characters.';
    if (!/^\w*$/.test(u)) return 'Your portfolio URL may contain only letters, numbers, and underscores.';
    return null;
  };

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      await uploadAvatar(file);
      flash('Photo uploaded', true);
      load();
    } catch { flash('Failed to upload photo', false); }
    if (fileRef.current) fileRef.current.value = '';
  };

  const handlePhotoDelete = async () => {
    try {
      await deleteAvatar();
      flash('Photo deleted', true);
      load();
    } catch { flash('Failed to delete photo', false); }
  };

  const handleSave = async () => {
    // Portfolio users must keep a valid username — legacy enforced this with a
    // `required: true, minlength: 5` field, so block the save (and surface the
    // reason) instead of letting an invalid value through. Without this guard a
    // portfolio user could blank their own username, silently breaking their
    // public `<username>.contently.com` URL.
    if (isPortfolio) {
      const err = usernameError();
      if (err) { flash(err, false); return; }
    }

    setSaving(true);
    // parity-audit-r2 (H2): resolve the discrete city/state/country the BE persists.
    // Prefer geocoded components (from Places autocomplete or seeded from the
    // profile). If the user typed a fresh free-text location that no longer matches
    // the geocoded value (and geocode is unavailable), fall back to splitting the
    // "City, State, Country" string so the change is never silently dropped. Legacy
    // stored these via current_residence_attributes — we send the same keys.
    const typed = form.location.trim();
    const geocodedFormatted = [residence.city, residence.state, residence.country]
      .filter(Boolean).join(', ');
    let loc = residence;
    if (typed !== geocodedFormatted) {
      // Free-text changed and wasn't geocoded — parse it positionally.
      const parts = typed.split(',').map((s) => s.trim());
      loc = { city: parts[0] || '', state: parts[1] || '', country: parts[2] || '' };
    }
    try {
      await updateProfile({
        user: {
          first_name: form.firstName, last_name: form.lastName, email: form.email,
          company: form.company, freelancer_type: form.freelancerType,
          website_url: form.websiteUrl, phone_number: form.phoneNumber,
          time_zone: form.timeZone,
          // parity-audit-r2 (H2): persist location via current_residence_attributes
          // — User#city/state/country are READ-ONLY delegations to current_residence
          // (no flat setters), so legacy wrote through the nested residence. Sending
          // flat city/state/country would be permitted-but-ignored (still data loss).
          // ALWAYS send (even when blank) so CLEARING the field actually persists —
          // legacy submitted the hidden current_residence fields unconditionally via
          // `fields_for :current_residence`, and the association has no reject_if.
          current_residence_attributes: { city: loc.city, state: loc.state, country: loc.country },
          // Only portfolio users edit the username (the field is hidden for
          // everyone else, matching the legacy `if current_user.portfolio?`
          // gate). For a non-portfolio user we OMIT the key entirely so the BE
          // never touches the column — exactly as legacy did when the input
          // wasn't rendered. Portfolio users always have a non-blank value here
          // (validated above), so '' never reaches the unique index
          // (eng-maintenance#20940).
          ...(isPortfolio ? { username: form.username.trim() } : {}),
          headline: form.headline, bio: form.bio,
          language_ids: form.selectedLanguageIds,
          social_link_attributes: {
            facebook_url: form.facebookUrl, twitter_url: form.twitterUrl,
            linkedin_url: form.linkedinUrl, tumblr_url: form.tumblrUrl,
            instagram_url: form.instagramUrl,
          },
        },
        from_portfolio: true,
      });
      flash('Your profile was successfully updated.', true);
      load();
    } catch (error: any) {
      // Surface the real reason instead of a blanket "not updated" lie. The BE
      // returns 422 with `{ errors: [...] }` (full_messages) on a genuine
      // validation failure — show those so the user can fix the offending field,
      // matching what classic rendered inline. Report anything else to Honeybadger
      // (a bare `catch {}` here previously swallowed the error — it never reached
      // HB and always claimed the save failed even when it succeeded).
      // (eng-maintenance#20945 follow-up.)
      const messages = isAxiosError(error)
        ? error.response?.data?.errors
        : undefined;
      if (Array.isArray(messages) && messages.length) {
        flash(messages.join('. '), false);
      } else {
        logError(error instanceof Error ? error : new Error(`Portfolio profile save failed: ${error}`));
        flash('An error occurred and your profile was not updated.', false);
      }
    } finally { setSaving(false); }
  };

  const freelancerTypes = profile?.formOptions?.freelancerTypes || [];
  const timeZones = profile?.formOptions?.timeZones || [];

  return (
    <EditPortfolioLayout>
      <PageTitle>Profile</PageTitle>

      <Row>
        <Label>Photo</Label>
        <PhotoRow>
          <PhotoPreview $url={profile?.avatarUrl || undefined} />
          <PhotoBtns>
            <PhotoBtn>Upload photo<input ref={fileRef} type="file" accept="image/jpeg,image/png,image/gif" onChange={handlePhotoUpload} /></PhotoBtn>
            {profile?.avatarUrl && <DeleteBtn onClick={handlePhotoDelete}>Delete photo</DeleteBtn>}
          </PhotoBtns>
        </PhotoRow>
      </Row>

      <NameRow>
        <div><Label $req>First</Label><Input value={form.firstName} onChange={e => set('firstName', e.target.value)} maxLength={255} /></div>
        <div><Label $req>Last</Label><Input value={form.lastName} onChange={e => set('lastName', e.target.value)} maxLength={255} /></div>
      </NameRow>

      <Row><Label $req>Email</Label><Input type="email" value={form.email} onChange={e => set('email', e.target.value)} maxLength={255} /></Row>

      <Row><Label>Company</Label><Input value={form.company} onChange={e => set('company', e.target.value)} maxLength={125} /></Row>

      <Row>
        <Label $req>Primary skill</Label>
        <Select value={form.freelancerType} onChange={e => set('freelancerType', e.target.value)}>
          <option value="">Select primary skill</option>
          {freelancerTypes.map(([label, value]) => <option key={value} value={value}>{label}</option>)}
        </Select>
      </Row>

      <Row><Label>Personal website url</Label><Input type="url" placeholder="Enter the url for your website" value={form.websiteUrl} onChange={e => set('websiteUrl', e.target.value)} /></Row>

      <Row>
        <LabelSub><Label $req>Phone number</Label><Sub>Only seen by Contently and brands you agree to work with.</Sub></LabelSub>
        <Input placeholder="+1 (888) 888-8888" value={form.phoneNumber} onChange={e => set('phoneNumber', e.target.value)} maxLength={35} />
      </Row>

      {/* parity-audit-r2 (H2): ref drives Google Places autocomplete when a token
          is provisioned; otherwise the free-text value is parsed on save. The hint
          mirrors the legacy "specific location" subtext shown with the geocoder. */}
      <Row>
        <Label $req>Location</Label>
        <Input ref={locationRef} placeholder="Anywhere..." value={form.location} onChange={e => set('location', e.target.value)} />
        {geocodeAvailable && (
          <Sub>Start typing and pick your city for the most accurate location.</Sub>
        )}
      </Row>

      <Row>
        <Label $req>Time zone</Label>
        <Select value={form.timeZone} onChange={e => set('timeZone', e.target.value)}>
          <option value="">Select timezone</option>
          {timeZones.map(tz => <option key={tz.name} value={tz.name}>{`(${tz.offset}) ${tz.name}`}</option>)}
        </Select>
      </Row>

      {/* The portfolio URL (username) is a portfolio-only, required field —
          legacy rendered it under `if current_user.portfolio?` with
          `required: true, minlength: 5`. Hidden for non-portfolio users so
          their username column is never submitted (eng-maintenance#20940). */}
      {isPortfolio && (
        <Row>
          <Label $req>Contently Portfolio URL</Label>
          <UrlRow><Input value={form.username} onChange={e => set('username', e.target.value)} minLength={5} maxLength={40} style={{ flex: 1 }} /><UrlSuffix>.contently.com</UrlSuffix></UrlRow>
        </Row>
      )}

      <Divider>
        <SectionTitle>Profile details</SectionTitle>
        <Row><Label>Title</Label><Input placeholder="I'm a writer and infographic designer." value={form.headline} onChange={e => set('headline', e.target.value)} maxLength={35} /></Row>
        <Row><Label>Bio</Label><Textarea placeholder="Write a brief bio of your professional experience." value={form.bio} onChange={e => set('bio', e.target.value)} maxLength={500} /></Row>
        <Row>
          <Label>You're fluent in...</Label>
          <Select multiple value={form.selectedLanguageIds.map(String)} onChange={e => { set('selectedLanguageIds', Array.from(e.target.selectedOptions, o => Number(o.value))); }} style={{ minHeight: 80 }}>
            {languages.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
          </Select>
        </Row>
      </Divider>

      <Divider>
        <SectionTitle>Social networks</SectionTitle>
        {SOCIAL.map(s => (
          <SocialRow key={s.key}>
            <SocialCircle $color={s.color}>{s.icon}</SocialCircle>
            <SocialInput placeholder={s.placeholder} value={form[s.key as SocialKey]} onChange={e => set(s.key, e.target.value)} />
          </SocialRow>
        ))}
      </Divider>

      <Actions><SaveBtn onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save'}</SaveBtn></Actions>
      {toast && <Toast $ok={toast.ok}>{toast.msg}</Toast>}
    </EditPortfolioLayout>
  );
}

export default PortfolioProfilePage;
